import React from "react";
import useSWR from "swr";
import api from "@/lib/api";
import { gbp, ddmmyyyy, dayShort } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { Bell, TrendingUp } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const DriverHome = () => {
  const { user } = useAuth();
  const { data, isLoading, error } = useSWR("/driver-portal/me", fetcher);

  if (isLoading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (error || !data) return <div className="p-6 text-sm text-red-600">Failed to load</div>;

  const { driver, period, summary, recent_work } = data;

  return (
    <div className="pb-4">
      {/* Header */}
      <div className="px-5 pt-6 pb-5 bg-[var(--brand-navy)] text-white rounded-b-3xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Welcome back</div>
            <div className="font-heading text-2xl font-bold tracking-tight mt-1">{driver.full_name}</div>
            <div className="text-xs text-white/60 mt-0.5 font-mono">
              {driver.driver_code} · <span className="text-emerald-400">Active subcontractor</span>
            </div>
          </div>
          <button className="w-10 h-10 rounded-full bg-white/10 grid place-items-center" data-testid="driver-notif-btn">
            <Bell className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Pay period hero */}
      {period && summary && (
        <div className="px-4 -mt-6">
          <Card className="rounded-2xl border-brand-border p-5 shadow-md" data-testid="driver-period-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Current period</div>
                <div className="font-heading text-base font-bold tracking-tight text-brand-text">{period.name}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">Pays {ddmmyyyy(period.payment_date)}</div>
              </div>
              <Badge className="bg-brand-red hover:bg-brand-red text-white uppercase text-[10px] tracking-wider">
                {period.status}
              </Badge>
            </div>
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Expected net pay</div>
              <div className="flex items-baseline gap-2 mt-1">
                <div className="font-heading text-4xl font-bold tracking-tight text-brand-text leading-none" data-testid="driver-expected-pay">
                  {gbp(summary.net_pay)}
                </div>
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Days</div>
                <div className="font-heading text-lg font-bold text-brand-text">{summary.days}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Stops</div>
                <div className="font-heading text-lg font-bold text-brand-text">{summary.stops.toLocaleString("en-GB")}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Parcels</div>
                <div className="font-heading text-lg font-bold text-brand-text">{summary.parcels.toLocaleString("en-GB")}</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Recent work */}
      <div className="px-4 mt-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Recent work</div>
          <div className="text-[10px] font-mono text-slate-400">Last 10 days</div>
        </div>
        <div className="space-y-2" data-testid="driver-recent-work">
          {(recent_work || []).map((r) => (
            <Card key={r.id} className="p-4 rounded-xl border-brand-border flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-brand-text">{dayShort(r.work_date)}</div>
                <div className="text-[11px] text-slate-500 font-mono mt-0.5">{r.route} · {r.stops} stops · {r.parcels} parcels</div>
              </div>
              <div className="text-right">
                <div className="font-heading font-bold text-brand-text">{gbp(r.route_payment)}</div>
              </div>
            </Card>
          ))}
          {(recent_work || []).length === 0 && (
            <Card className="p-6 rounded-xl border-brand-border text-center text-xs text-slate-500">No work rows yet.</Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverHome;
